import React from "react";
import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionCard,
  DecisionList,
} from "@/components/DecisionPageLayout";
import { FileText, ShieldCheck, Phone } from "lucide-react";
import { InlineRelatedLinks } from "@/components/RelatedPages";

const HowInsuranceWorks = () => (
  <DecisionPageLayout
    seo={{
      title: "How Insurance Works for SAINT TMS | Neuro Wellness",
      description:
        "Verifying benefits and obtaining authorization are different processes. Learn the sequence — consultation, documentation, authorization, decision, appeal — and what to prepare.",
      canonical: "/how-insurance-works",
      keywords:
        "SAINT TMS insurance, TMS prior authorization, insurance verification TMS Florida, SAINT coverage",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: "How Insurance Actually Works Here",
        about:
          "Insurance verification and authorization process for SAINT TMS therapy",
      },
    }}
    badge="Insurance Guide"
    title="How Insurance Actually Works Here"
    subtitle="Verifying benefits and obtaining authorization are two different things. Here's the real sequence."
  >
    <DecisionSection heading="The Distinction That Matters Most">
      <p>
        Verifying your benefits tells us what your plan covers in theory.
        Obtaining authorization is the process of getting your plan to agree to
        cover your specific treatment. These are separate steps, and both take
        time.
      </p>
    </DecisionSection>

    <DecisionCard>
      <div className="flex items-center gap-3">
        <FileText className="h-6 w-6 text-accent shrink-0" />
        <DecisionSubHeading>The Sequence, Step by Step</DecisionSubHeading>
      </div>
      <DecisionList
        items={[
          "Consultation — You meet with our clinical team. We review your history and confirm whether SAINT is clinically appropriate.",
          "Documentation gathering — We collect the records your insurer requires: treatment history, prior medication trials, and clinical notes.",
          "Authorization request — We submit the prior authorization request to your insurance carrier on your behalf.",
          "Decision — The insurer reviews the request and issues a decision. This can take days to weeks depending on the carrier.",
          "Appeal if needed — If the initial request is denied, we submit an appeal. We handle this process for you.",
        ]}
      />
    </DecisionCard>

    <DecisionSection>
      <DecisionSubHeading>What We Handle</DecisionSubHeading>
      <div className="flex gap-3 items-start">
        <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-1" />
        <p>
          Our team submits the authorization request and handles appeals on your
          behalf. You do not call the insurance company yourself. We track the
          process and keep you informed at each step.
        </p>
      </div>
    </DecisionSection>

    <DecisionSection>
      <DecisionSubHeading>Which Plans Have Options</DecisionSubHeading>
      <p>
        Some plans — including Blue Cross Blue Shield, AmeriHealth, and
        Independence Blue Cross — have options within their policies for
        accelerated TMS. Coverage varies by specific plan and is reviewed case
        by case.
      </p>
      <p>
        Medicaid and Medicare Advantage plans generally don't cover SAINT at
        this time. If you have one of these plans, we'll discuss this with you
        honestly during the consultation.
      </p>
    </DecisionSection>

    <DecisionCard>
      <DecisionSubHeading>What You Can Prepare in Advance</DecisionSubHeading>
      <DecisionList
        items={[
          "Your insurance card (front and back)",
          "A list of antidepressant medications you have tried, with approximate dates and doses if possible",
          "Records from your current or most recent psychiatrist or prescriber",
          "The approximate end date of any prior TMS treatment",
          "Your primary care provider's contact information",
        ]}
      />
    </DecisionCard>

    <DecisionSection>
      <div className="flex gap-3 items-start">
        <Phone className="h-5 w-5 text-accent shrink-0 mt-1" />
        <p>
          Insurance questions are some of the most common ones we hear. Call us
          at (954) 827-9793 and we'll walk you through what to expect for your
          specific plan.
        </p>
      </div>
    </DecisionSection>

    <InlineRelatedLinks
      heading="Explore Related Pages"
      links={[
        { title: "SAINT® Accelerated TMS", href: "/saint" },
        { title: "Cost & Insurance Options", href: "/cost-insurance" },
        { title: "Am I a Candidate?", href: "/am-i-a-candidate" },
        { title: "Compare All Treatments", href: "/compare-treatments" },
        { title: "Traveling for Treatment", href: "/traveling-for-treatment" },
      ]}
    />
  </DecisionPageLayout>
);

export default HowInsuranceWorks;
